import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { TokenModel } from '../../models/token-model';
import { WorkerRole } from '../../models/enums/worker-role.enum';
import { environment } from '../../../environments/environment';
import { AuthContext } from '../../models/auth/auth-context.model';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly tokenKey = 'authToken';
  private readonly contextStorageKey = 'authContext';

  constructor() {}

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    this.createContext(token);
  }

  private createContext(token: string): void {
    try {
      const decoded: TokenModel = jwtDecode(token);

      const context: AuthContext = {
        token,
        businessId: Number(decoded.BusinessId),
        role: WorkerRole[decoded.Role as keyof typeof WorkerRole],
        employeeId: Number(decoded.EmployeeId)
      };
      localStorage.setItem(this.contextStorageKey, JSON.stringify(context));
    }
    catch (e) {
      console.log("Invalid token!");
      console.error(e);
    }
  }

  getBusinessId(): number {
    const context = this.readContextFromCache();
    if (!context)
      throw new Error("Invalid token!");

    return context.businessId;
  }

  getEmployeeId(): number {
    const context = this.readContextFromCache();
    if (!context)
      throw new Error("Invalid token!");

    return context.employeeId ?? 0;
  }

  getRole(): WorkerRole {
    const context = this.readContextFromCache();
    if (!context)
      throw new Error("Invalid token!");

    return context?.role;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !this.isTokenExpired(token || '');
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.contextStorageKey);
    window.location.href = environment.mainWebsite;
  }

  private isTokenExpired(token: string): boolean {
    if (!token)
      return true;

    try {
      const decoded: any = jwtDecode(token);
      const exp = decoded.exp * 1000;
      if (Date.now() > exp) {
        console.log("Token expired!");
        this.logout();
        return true;
      }
      console.log("Token is valid!");
      return false;
    }
    catch (e) {
      console.log("Invalid token!");
      this.logout();
      return true;
    }
  }

  private readContextFromCache(): AuthContext | null {
    const raw = localStorage.getItem(this.contextStorageKey);

    if (!raw) {
      return null;
    }

    try {
      const context = JSON.parse(raw) as AuthContext;

      if (!context.token || this.isTokenExpired(context.token)) {
        return null;
      }

      return context;
    } catch {
      return null;
    }
  }
}
